import { toast } from "@/components/Toast";
import { checkImagesInAlbum, PostAddAlbumType, postImageAlbum } from "@/modules/album/api";
import { parseLabels } from "@/utils/misc";
import { Button, chakra, Flex, Text, useBoolean, usePrevious } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelectImages } from "../../hooks/useSelectImages";
import { albumStore } from "@/modules/album/store";
import { ReactComponent as ArrowSvg } from "@/assets/icons/left_arrow.svg";
import Icon from "@/components/Icon";
import { setRightPanelTab, uiStore } from "@/stores/ui";
import { inspirationStore } from "../../stores/inspiration";
import { observer } from "mobx-react-lite";
import { WithClassName } from "@/types/base";
import { BoardImageDetailModal } from "@/components/bussiness/BoardImageDetail";
import { Editor_Album_Words, useTranslate } from "@/utils/lang";
import { useTabActive } from "../../hooks/useTabActive";

const checkArrayEqual = (
  arr1: Array<string | number | boolean>,
  arr2: Array<string | number | boolean>,
) => {
  const set2 = new Set(arr2);
  return arr1.length == arr2.length && arr1.every((alias) => set2.has(alias));
};
const IAlbumCollector: React.FC<WithClassName> = ({ className }) => {
  const { translate } = useTranslate();
  const { imageNodes } = useSelectImages();
  const [modalOpen, modalOp] = useBoolean(false);
  const [collectedMap, setCollectedMap] = useState<Record<string, boolean>>({});
  const aliases = useMemo(() => imageNodes.map((node) => node.alias), [imageNodes]);
  const prevAliases = usePrevious(aliases) ?? [];
  const isDiff = useMemo(() => !checkArrayEqual(aliases, prevAliases), [aliases, prevAliases]);
  const { rightPanelTab } = uiStore;
  const { isTabChangedAndActive } = useTabActive(0, rightPanelTab);

  const [isChecking, checkingOp] = useBoolean(true);

  const checkCollected = useCallback(
    (aliases: string[]) => {
      return checkImagesInAlbum(aliases)
        .then((res) => res)
        .then((res) => {
          if (res.code === 0) {
            return res.data.is_in_album_map;
          } else {
            return Promise.reject(res);
          }
        })
        .catch((err) => {
          console.error("checkImagesInAlbum err: ", err);
          return aliases.reduce<Record<string, boolean>>((map, alias) => {
            map[alias] = false;
            return map;
          }, {});
        });
    },
    [collectedMap],
  );

  const checkImageNodesCollected = useCallback(() => {
    checkingOp.on();
    // if (checkArrayEqual(aliases, prevAliases)) {
    //   checkingOp.off();
    //   return;
    // }
    checkCollected(aliases)
      .then((result) => {
        setCollectedMap(result);
      })
      .finally(() => {
        checkingOp.off();
      });
  }, [aliases, prevAliases, checkCollected, checkingOp, setCollectedMap]);

  const collectableImageNodes = useMemo(() => {
    return imageNodes.filter((node) => !collectedMap[node.alias]);
  }, [collectedMap, imageNodes]);
  const collectable = useMemo(() => collectableImageNodes.length > 0, [collectableImageNodes]);

  useEffect(() => {
    if (isDiff) {
      checkImageNodesCollected();
    }
  }, [isDiff]);

  useEffect(() => {
    if (isTabChangedAndActive) {
      checkImageNodesCollected();
    }
  }, [isTabChangedAndActive]);

  const handleMultiClick = async (alias: string) => {
    const {
      data: { albums },
    } = await postImageAlbum({
      board_alias: alias,
      type: "board",
    }).catch((err) => {
      console.error(err, "postImageAlbum error");
    });

    const postedAlbum = {
      id: albums?.[0]?.id,
      name: albums?.[0]?.name,
    };

    if (!postedAlbum?.id) return;
    inspirationStore.setCurTab("album");
    albumStore.setCurSelectAlbum(postedAlbum);
    setRightPanelTab(1);
  };

  const handleSingleClick = () => {
    modalOp.on();
  };

  const collectToAlbum = useCallback(async () => {
    // 如果单张/多张图片都已经被收藏过
    if (!collectable) {
      const isMulti = imageNodes.length > 1;
      if (isMulti) {
        handleMultiClick(imageNodes[0].alias);
      } else {
        handleSingleClick();
      }
      return;
    }
    // 收藏还没被收藏的图片
    const batchData = collectableImageNodes.map<PostAddAlbumType>((node) => {
      return {
        board_alias: node.alias,
        meta_json: JSON.stringify(node?.params.meta),
        image_url: node.renderParams.src,
        source: "board",
        is_ai: parseLabels(node.params.meta)?.isAI ? 1 : 2,
      };
    });

    albumStore
      .addPictures(batchData)
      .then((res) => res)
      .then(async (res) => {
        if (res.code === 0) {
          const isMulti = imageNodes.length > 1;

          setCollectedMap((map) => {
            const newMap = Object.assign({}, map);
            batchData.forEach((item) => {
              newMap[item.board_alias!] = true;
            });
            return newMap;
          });

          toast({
            status: "success",
            duration: 6000,
            title: (
              <Flex alignItems="center" justifyContent="center" fontSize="14px">
                <Text mr="110px">{translate(Editor_Album_Words.add_success_toast)}</Text>
                {isMulti ? (
                  <Button
                    variant="link"
                    display="inline-flex"
                    color="white"
                    fontWeight="bold"
                    _hover={{ color: "white" }}
                    onClick={() => handleMultiClick(batchData[0].board_alias!)}
                  >
                    {translate(Editor_Album_Words.arrangeInAlbum)}
                    <Icon
                      svg={ArrowSvg}
                      transform="rotate(180deg)"
                      squared={false}
                      strokeByCurrentColor
                    />
                  </Button>
                ) : (
                  <Button
                    variant="link"
                    display="inline-flex"
                    color="white"
                    fontWeight="bold"
                    _hover={{ color: "white" }}
                    onClick={handleSingleClick}
                  >
                    {translate(Editor_Album_Words.editAlbumGroup)}
                    <Icon
                      svg={ArrowSvg}
                      transform="rotate(180deg)"
                      squared={false}
                      strokeByCurrentColor
                    />
                  </Button>
                )}
              </Flex>
            ),
          });
        }
      })
      .catch(() => {
        toast({
          status: "error",
          title: translate(Editor_Album_Words.add_failed_toast),
        });
      });
  }, [collectable, imageNodes, collectableImageNodes, handleMultiClick, handleSingleClick]);

  return (
    <>
      <Button
        className={className}
        w="100%"
        flex={1}
        variant="outline"
        isLoading={isChecking}
        isDisabled={isChecking}
        onClick={collectToAlbum}
      >
        {collectable
          ? translate(Editor_Album_Words.addToAlbum)
          : translate(Editor_Album_Words.editAlbumGroup)}
      </Button>
      {imageNodes?.[0] && (
        <BoardImageDetailModal
          open={modalOpen}
          onClose={() => {
            modalOp.off();
          }}
          onAddToAlbum={() => {
            setCollectedMap((map) => {
              const newMap = Object.assign({}, map);
              newMap[imageNodes[0].alias] = true;
              return newMap;
            });
          }}
          onRemove={() => {
            setCollectedMap((map) => {
              const newMap = Object.assign({}, map);
              newMap[imageNodes[0].alias] = false;
              return newMap;
            });
          }}
          alias={imageNodes[0].alias}
        />
      )}
    </>
  );
};

export const AlbumCollector = chakra(observer(IAlbumCollector));
